import React, { useState, useEffect } from 'react';
import { Tag, Button, Select, Tooltip, Form, Drawer, Divider, Descriptions } from 'antd';
import Icons from '@/components/third-party/Icons';
import DataTable from '@/components/DataTable';
import { MapPin, Clock, Edit, Eye } from 'lucide-react';
import { get_employee_user_list, add_employee_user, get_shift, get_warehouse_areas_dropdown } from '@/utils/api';
import { toast } from 'react-toastify';
import { EmptyState } from '@/components/PageHeader';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import PageModal from '@/components/PageModal';

const { Option } = Select;

const UserAccessManagement = () => {
  const { isDark } = useTheme();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editModal, setEditModal] = useState({ open: false, record: null, submitting: false });
  const [detailRecord, setDetailRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchEmployees = () => {
    setLoading(true);
    get_employee_user_list((res) => {
      setLoading(false);
      if (res?.status === 'success') setEmployees(res.data || []);
    });
  };

  useEffect(() => {
    fetchEmployees();
    get_shift((res) => setShifts(res?.data?.data || []));
    get_warehouse_areas_dropdown((res) => setLocations(res?.data?.data || []));
  }, []);

  const openEdit = (record) => {
    form.setFieldsValue({
      shift_id: record.shifts?.length ? record.shifts : (record.shift_id ? [record.shift_id] : []),
      location_id: record.locations?.length ? record.locations : (record.location_id ? [record.location_id] : []),
    });
    setEditModal({ open: true, record, submitting: false });
  };

  const closeModal = () => setEditModal({ open: false, record: null, submitting: false });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setEditModal((prev) => ({ ...prev, submitting: true }));
      const result = await add_employee_user(
        {
          id: editModal.record.employee_id,
          shift_id: values.shift_id,
          location_id: values.location_id,
        },
        (res) => {
          closeModal();
          if (res?.status === 'success') {
            toast.success('Updated successfully');
            fetchEmployees();
          } else {
            toast.error(res?.message || 'Failed to update');
          }
        }
      );
      // handleResponse only calls the callback on 2xx; close for non-2xx responses
      if (result && !result.ok) closeModal();
    } catch (e) {
      closeModal();
    }
  };

  const parseRoleNames = (role_name) => {
    try {
      if (Array.isArray(role_name)) return role_name;
      return JSON.parse(role_name.replace(/'/g, '"'));
    } catch {
      return [role_name];
    }
  };

  const columns = [
    {
      title: '#',
      key: 'idx',
      width: 50,
      align: 'center',
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'User ID',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (t) => <span style={{ fontWeight: 500 }}>{t || '—'}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: 170,
      render: (t) => <span style={{ fontWeight: 500 }}>{t || '—'}</span>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      render: (t) => t ? <Tag>{t}</Tag> : '—',
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
      width: 150,
      render: (t) => t ? <Tag>{t}</Tag> : '—',
    },
    {
      title: 'Roles',
      dataIndex: 'role_name',
      key: 'role_name',
      width: 220,
      render: (role_name) => {
        const names = parseRoleNames(role_name || '[]');
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {names.map((r, i) => <Tag key={i}>{r}</Tag>)}
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <div className="flex justify-center" style={{ gap: 6 }}>
          <Tooltip title="View Details">
            <Button
              size="small"
              aria-label={`View details of ${record.employee_name}`}
              icon={<Icons.Eye className="w-4 h-4" style={{ color: defaultColors.primary }} />}
              onClick={() => setDetailRecord(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Shift & Location">
            <Button
              size="small"
              aria-label={`Edit shift and location of ${record.employee_name}`}
              icon={<Icons.Pencil className="w-4 h-4 text-amber-600" />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        tableKey="userAccessManagementTable"
        searchable
        searchKeys={['employee_name', 'department', 'location', 'username']}
        searchPlaceholder="Search by name, department, location…"
        columnConfig
        showCount={false}
        exportable
        exportFilename="user_access"
        onRefresh={fetchEmployees}
        className="user-access-management-table"
        rowKey="employee_id"
        data={employees}
        locale={{ emptyText: <EmptyState title="No users found" description="Users appear here once they are created under the Users tab." /> }}
        columns={columns}
        loading={loading}
        bordered
        scrollX="max-content"
        height={null}
      />

      {/* Edit Modal — only shift & location editable */}
      <PageModal
        open={editModal.open}
        icon={<Edit size={22} />}
        title="Edit Access"
        description={editModal.record?.employee_name}
        onCancel={() => setEditModal({ open: false, record: null, submitting: false })}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditModal({ open: false, record: null, submitting: false })}>Cancel</Button>
            <Button type="primary" loading={editModal.submitting} onClick={handleSave}>Save</Button>
          </div>
        }
        width={520}
        destroyOnHidden
      >
        {/* Read-only info */}
        <div style={{ background: isDark ? '#1f2937' : '#f9fafb', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>User ID:</span>
            <span style={{ fontWeight: 500 }}>{editModal.record?.username}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>Name:</span>
            <span style={{ fontWeight: 500 }}>{editModal.record?.employee_name}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>Department:</span>
            <Tag>{editModal.record?.department || '—'}</Tag>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>Roles:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {parseRoleNames(editModal.record?.role_name || '[]').map((r, i) => (
                <Tag key={i}>{r}</Tag>
              ))}
            </div>
          </div>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            label={<span><Clock size={14} style={{ marginRight: 5, verticalAlign: 'middle', color: defaultColors.primary }} />Shift</span>}
            name="shift_id"
          >
            <Select mode="multiple" placeholder="Select Shift" allowClear showSearch style={{ width: '100%' }}
              optionFilterProp="children"
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            >
              {shifts.map((s) => (
                <Option key={s.shift_id} value={s.shift_id}>{s.shift}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span><MapPin size={14} style={{ marginRight: 5, verticalAlign: 'middle', color: defaultColors.primary }} />Location (Warehouse Area)</span>}
            name="location_id"
          >
            <Select mode="multiple" placeholder="Select Location" allowClear showSearch style={{ width: '100%' }}
              optionFilterProp="children"
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            >
              {locations.map((l) => (
                <Option key={l.warehouse_area_id || l.location_id || l.id} value={l.warehouse_area_id || l.location_id || l.id}>
                  {l.area_name || l.location_name || l.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </PageModal>

      {/* Details Drawer */}
      <Drawer
        title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700 }}><Eye size={18} style={{ color: defaultColors.primary }} />Details — {detailRecord?.employee_name}</span>}
        placement="right"
        width={440}
        classNames={{ body: 'page-card-scroll' }}
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        mask={false}
        zIndex={2001}
      >
        {detailRecord && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={13} /> Shifts
              </div>
              {detailRecord.shifts_names?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detailRecord.shifts_names.map((name, i) => (
                    <Tag key={i} style={{ margin: 0 }}>{name}</Tag>
                  ))}
                </div>
              ) : detailRecord.shift && detailRecord.shift !== 'None-None' ? (
                <Tag>{detailRecord.shift}</Tag>
              ) : (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No shifts assigned</span>
              )}
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={13} /> Locations
              </div>
              {detailRecord.locations_names?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detailRecord.locations_names.map((name, i) => (
                    <Tag key={i} style={{ margin: 0 }}>{name}</Tag>
                  ))}
                </div>
              ) : detailRecord.location ? (
                <Tag>{detailRecord.location}</Tag>
              ) : (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No locations assigned</span>
              )}
            </div>

            <Divider style={{ margin: '4px 0' }} />

            <Descriptions size="small" column={1} bordered styles={{ label: { width: 130, background: isDark ? defaultColors.palette.gray[700] : '#f9fafb', fontWeight: 600 }, content: { background: isDark ? defaultColors.palette.gray[800] : '#ffffff' } }}>
              <Descriptions.Item label="Username">{detailRecord.username}</Descriptions.Item>
              <Descriptions.Item label="Email">{detailRecord.email}</Descriptions.Item>
              <Descriptions.Item label="Contact">{detailRecord.contact}</Descriptions.Item>
              <Descriptions.Item label="Department">{detailRecord.department}</Descriptions.Item>
              <Descriptions.Item label="Designation">{detailRecord.designation}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default UserAccessManagement;
